<template>
    <div class="w-100">
        <div v-if="allowSearch" class="field has-addons is-horizontal is-pulled-right">
            <div class="control">
                <input
                    class="input"
                    type="text"
                    v-on:keypress.enter="search"
                    v-model="searchValue"
                    :placeholder="$t('data-viewer.insert-text')"
                />
            </div>
            <div class="control">
                <button class="button is-primary" @click="search">
                    {{ $t("data-viewer.search") }}
                </button>
            </div>
        </div>

        <table :class="css.tableClass">
            <thead>
                <tr>
                    <th
                        scope="col"
                        class="has-text-primary"
                        v-for="(field, idx) in fields"
                        :key="idx"
                    >
                        {{ field.displayText }}
                    </th>
                </tr>
            </thead>
            <tbody>
                <template v-for="(item, idx) in tableItems">
                    <Row
                        :key="idx"
                        :item="item"
                        :fields="fields"
                        :onClickRow="onRowSelect"
                        @reloadTable="reloadTable"
                    />
                </template>
                <tr v-if="loadingContent">
                    <td class="has-text-centered" :colspan="fields.length">
                        <div class="spinner">
                            <div class="bounce1"></div>
                            <div class="bounce2"></div>
                            <div class="bounce3"></div>
                        </div>
                    </td>
                </tr>
                <tr v-else-if="tableItems.length === 0">
                    <td class="has-text-centered" :colspan="fields.length">
                        {{ $t("data-viewer.no-rows") }}
                    </td>
                </tr>
            </tbody>
        </table>

        <nav
            :class="css.navPaginationClass"
            role="navigation"
            aria-label="pagination"
            v-if="totalPages > 1"
        >
            <a
                :class="css.navPrevClass"
                v-if="currentPage > 1"
                v-on:click="prev()"
                >Previous</a
            >
            <a
                :class="css.navPrevClass"
                v-if="currentPage < totalPages"
                v-on:click="next()"
                >Next page</a
            >

            <ul class="pagination-list">
                <li v-if="currentPage > 2">
                    <a
                        :class="css.navPaginationLink"
                        v-on:click="goto(1)"
                        aria-label="Goto page 1"
                        >1</a
                    >
                </li>

                <li v-if="currentPage > 3">
                    <span class="pagination-ellipsis">&hellip;</span>
                </li>
                <li v-if="currentPage > 1">
                    <a
                        :class="css.navPaginationLink"
                        v-on:click="goto(currentPage - 1)"
                        :aria-label="'Goto page ' + (this.currentPage - 1)"
                        >{{ currentPage - 1 }}</a
                    >
                </li>
                <li>
                    <a
                        :class="css.navCurrentPaginationLink"
                        v-on:click="goto(currentPage)"
                        :aria-label="'Goto page ' + this.currentPage"
                        aria-current="page"
                        >{{ currentPage }}</a
                    >
                </li>
                <li v-if="totalPages > currentPage">
                    <a
                        :class="css.navPaginationLink"
                        v-on:click="goto(currentPage + 1)"
                        :aria-label="'Goto page ' + (this.currentPage + 1)"
                        >{{ currentPage + 1 }}</a
                    >
                </li>
                <li v-if="totalPages - currentPage > 2">
                    <span class="pagination-ellipsis">&hellip;</span>
                </li>

                <li v-if="totalPages - currentPage > 1">
                    <a
                        :class="css.navPaginationLink"
                        v-on:click="goto(totalPages)"
                        :aria-label="'Goto page ' + this.totalPages"
                        >{{ this.totalPages }}</a
                    >
                </li>
            </ul>
        </nav>
    </div>
</template>

<script>
import Row from "./DataViewerRow";

export default {
    name: "data-viewer",
    components: {
        Row,
    },
    props: {
        onRowSelect: Function,
        css: {
            type: Object,
            default() {
                return {
                    tableClass: "table is-striped is-hoverable is-fullwidth",
                    navPaginationClass: "pagination",
                    navNextClass: "pagination-next",
                    navPrevClass: "pagination-previous",
                    navPaginationLink: "pagination-link",
                    navCurrentPaginationLink: "pagination-link is-current",
                };
            },
        },
        fields: {
            type: Array,
            required: true,
        },
        getPagedData: {
            type: Function,
            required: true,
        },
        notifyError: {
            type: Function,
        },
        take: {
            type: Number,
            default: 10,
        },
        allowSearch:{
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            tableItems: [],
            nextEndpoint: null,
            prevEndpoint: null,
            totalPages: 0,
            currentPage: 0,
            searchValue: "",
            loadingContent: false,
        };
    },
    mounted() {
        this.retrieveData({ take: this.take });
    },
    methods: {
        reloadTable() {
            this.retrieveData({ take: this.take });
        },
        async retrieveData(payload) {
            try {
                this.loadingContent = true;
                let response = await this.getPagedData(payload);
                let data = response.data ?? response;
                this.tableItems = data.items;
                this.nextEndpoint = data.nextUrl;
                this.prevEndpoint = data.prevUrl;
                this.totalPages = data.pages;
                this.currentPage = data.page;
                this.searchValue = data.search;
            } catch (err) {
                if (this.notifyError) this.notifyError(err);
            }
            this.loadingContent = false;
        },
        async prev() {
            if (this.prevEndpoint !== null) {
                this.retrieveData({
                    page: this.currentPage - 1,
                    search: this.searchValue,
                });
            }
        },
        next() {
            if (this.nextEndpoint !== null) {
                this.retrieveData({
                    page: this.currentPage + 1,
                    search: this.searchValue,
                });
            }
        },
        goto(page) {
            this.retrieveData({ page: page, search: this.searchValue });
        },
        renderCol(item, field) {
            if (field.ref !== undefined) {
                let refs = field.ref.split(".");

                for (var i = 0; i < refs.length; ++i) {
                    item = item[refs[i]];
                }

                return field.render !== undefined ? field.render(item) : item;
            } else {
                return field.render(item);
            }
        },
        search() {
            if (this.searchValue === null || this.searchValue === undefined) {
                this.searchValue = "";
            }
            this.retrieveData({ search: this.searchValue });
        },
    },
};
</script>

<style lang="scss" scoped>
.spinner {
    margin: 10px auto 10px;
    width: 70px;
    text-align: center;
}

.spinner > div {
    width: 15px;
    height: 15px;
    background-color: #333;

    border-radius: 100%;
    display: inline-block;
    -webkit-animation: sk-bouncedelay 1.4s infinite ease-in-out both;
    animation: sk-bouncedelay 1.4s infinite ease-in-out both;
}

.spinner .bounce1 {
    -webkit-animation-delay: -0.32s;
    animation-delay: -0.32s;
}

.spinner .bounce2 {
    -webkit-animation-delay: -0.16s;
    animation-delay: -0.16s;
}

@-webkit-keyframes sk-bouncedelay {
    0%,
    80%,
    100% {
        -webkit-transform: scale(0);
    }
    40% {
        -webkit-transform: scale(1);
    }
}

@keyframes sk-bouncedelay {
    0%,
    80%,
    100% {
        -webkit-transform: scale(0);
        transform: scale(0);
    }
    40% {
        -webkit-transform: scale(1);
        transform: scale(1);
    }
}
</style>